import { useState, useRef } from "react";
import { vibrate } from "../utils/platform";

export function useCamera() {
  const [cameraSupported, setCameraSupported] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [lastDetected, setLastDetected] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const detectorRef = useRef<any | null>(null);

  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const startCamera = async () => {
    setScannerError(null);
    setCameraDenied(false);
    setCameraSupported(!!navigator.mediaDevices?.getUserMedia);
    if (!navigator.mediaDevices?.getUserMedia) {
      setScannerError("Camera not supported on this device/browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      if ("BarcodeDetector" in window) {
        try { detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] }); } catch { detectorRef.current = null; }
      }
      setScanning(true);
      setScannerError(null); // Clear any previous errors since camera started successfully
      scanLoop();
    } catch (err: any) {
      if (err?.name === "NotAllowedError") {
        setCameraDenied(true);
        setScannerError("Camera access denied. You can enter the 8-digit code instead.");
      } else {
        setScannerError("Could not start camera. Try entering the 8-digit code.");
      }
    }
  };

  const scanLoop = async () => {
    if (!videoRef.current) return;
    const tick = async () => {
      if (detectorRef.current) {
        try {
          const codes = await detectorRef.current.detect(videoRef.current);
          if (codes?.length) {
            const raw = codes[0]?.rawValue || "";
            if (raw) { 
              vibrate(15);
              setLastDetected(raw);
              stopCamera();
              return; 
            }
          }
        } catch {}
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  return {
    cameraSupported,
    cameraDenied,
    scannerError,
    scanning,
    lastDetected,
    videoRef,
    startCamera,
    stopCamera,
    setLastDetected
  };
}
